import { Injectable } from '@angular/core';
import { createMarketplaceCartItem, createPortalProductCartItem } from './models/cart.model';
import { CartStore } from './cart.store';
import { CartQuery } from './cart.query';
import { MarketplaceItemExtended } from '../marketplace/models/marketplace-item-extended.model';
import { BasketService } from '../marketplace/basket.service';
import { MarketplaceBasketItem } from '../marketplace/models/basket/marketplace-basket-item.model';
import { MarketplaceService } from '../marketplace/marketplace.service';
import { MarketplaceQuery } from '../marketplace/marketplace.query';
import { BalanceService } from '../../account/balance/balance.service';
import { config } from '../../shared/config';
import { PortalProduct } from '../portal-products/models/portal-product.model';
import { TransactionSuccessService } from '../transaction-success/transaction-success.service';
import { MemberQuery } from '../../account/member/member.query';
import { tap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ID } from '@datorama/akita';
import { MarketplaceAttributeCartItem } from '../marketplace/models/basket/marketplace-attribute-cart-item.model';
import { MarketplaceBasketCustomFieldModel } from '../marketplace/models/basket/marketplace-basket-customfield.model';
import { MarketplaceItemV2 } from '../marketplace/marketplace-v2/models/marketplace-item-v2.model';

/**
 * This cart service will handle the updates of
 * adding and removing items to the cart store (for both
 * marketplaceitems and portal products).
 */
@Injectable({ providedIn: 'root' })
export class CartService {
  constructor(
    private cartStore: CartStore,
    private cartQuery: CartQuery,
    private basketService: BasketService,
    private marketplaceService: MarketplaceService,
    private marketplaceQuery: MarketplaceQuery,
    private balanceService: BalanceService,
    private transactionSuccessService: TransactionSuccessService,
    private memberQuery: MemberQuery,
    private router: Router
  ) {}

  /**
   * @param marketplaceItem  The marketplace item added to the cart
   * @param quantitySelected  The quantity of the item selected by the user
   * @param selectedAttribute  The attribute of the marketplace item added to the cart, if any
   * @param customFields  The custom fields of the marketplace item added to the cart, if any
   * @param updateFormCart  Update quantity from cart
   * @returns This method adds to the cart store with marketplace items and the quantity added
   * by the user. The conditions within this method handles the action
   * of adding new marktetplace items to the cart store as well as updating the
   * quantity of existing items within the cart store.
   */
  addMarketplaceItemToCart(
    marketplaceItem: MarketplaceItemExtended | MarketplaceItemV2,
    quantitySelected: number,
    attribute?: MarketplaceAttributeCartItem,
    customFields?: MarketplaceBasketCustomFieldModel[],
    updateFormCart?: boolean
  ) {
    const attributeID = attribute ? attribute.AttributeMappingId : undefined;
    const cartItem = this.cartQuery.getMemberMarketplaceCartItem(marketplaceItem.Id, attributeID, customFields);
    if (!!cartItem) {
      this.cartStore.updateQuantityOfItem(cartItem, quantitySelected, updateFormCart);
    } else {
      this.cartStore.add(
        createMarketplaceCartItem(
          marketplaceItem,
          quantitySelected,
          this.memberQuery.getUserContext().memberId,
          attribute,
          customFields
        )
      );
    }
  }

  removeAllCartItems() {
    return this.cartStore.remove();
  }

  /**
   * @param id  The unique id of the item being removed from the cart store.
   * @returns This method removes individual items from the cart store.
   */
  remove(id: ID) {
    this.cartStore.remove(id);
  }

  /**
   * @param totalPoints the total points to be deducted after purchasing marketplace items within the cart store
   * @returns This method is called to purchase the marketplace items within the cart.
   */
  async purchaseMarketplaceItemsInCart(totalPoints: number) {
    this.basketService.emptyBasket();
    this.cartQuery
      .selectAllMarketplaceCartData()
      .pipe(
        tap((cart) => {
          if (!cart) {
            return;
          }

          cart.forEach((cartItem) => {
            let item = this.marketplaceQuery.getEntity(cartItem.productData.Id);
            if (item !== undefined) {
              this.basketService.addItemToBasket(
                MarketplaceBasketItem.fromMarketplaceItem(
                  item,
                  cartItem.purchaseQuantity,
                  cartItem.attributes ? cartItem.attributes.AttributeMappingId : undefined,
                  cartItem.customFields
                )
              );
            }
          });
        }),
        take(1)
      )
      .toPromise()
      .then(() => {
        this.marketplaceService.purchaseBasket(this.basketService.getCurrentBasket().items).then(
          (orderIdResponse) => {
            this.balanceService.debitBalance(config.purseConfig.virtualLoyalty, totalPoints);
            this.basketService.getCurrentBasket().items.forEach((item) => {
              let marketplaceItem = this.marketplaceQuery.getEntity(item.id);
              if (marketplaceItem !== undefined) {
                this.transactionSuccessService.setTransaction(marketplaceItem, item.quantity, item.merchantValue);
              }
            });

            this.router.navigate(['checkout/results'], {
              queryParams: { orderId: orderIdResponse },
            });
          },
          (error) => {
            // if has an error,data will fetch agian to check real stock quantity for cart-item
            this.marketplaceService.disableMarketplaceStoreCache();
            this.marketplaceService.fetchMarketplaceItems().toPromise();
          }
        );
      });
  }

  /**
   * @param portalProduct The portal product added to cart
   * @returns This method adds to the cart store with portal product items
   * added by the user.
   */
  addPortalProductToCart(portalProduct: PortalProduct) {
    this.cartStore.add(createPortalProductCartItem(portalProduct, 1, this.memberQuery.getUserContext().memberId));
  }
}
